/**
 * Players — список игроков с поиском по почте и по id.
 * Клик по строке открывает карточку.
 */

import { useEffect, useState } from 'react'
import { request } from '../api/client.js'
import type { PlayerRow, PlayersResponse } from '../api/types.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { PageHead } from '../components/Layout.js'
import { Card, Money, formatDate } from '../components/ui.js'
import { navigate } from '../router.js'

const PAGE_SIZE = 25

export function Players(): JSX.Element {
  const [query, setQuery] = useState('')
  const [applied, setApplied] = useState('')
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState<PlayersResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    request<PlayersResponse>('/admin/players', {
      query: { q: applied, limit: PAGE_SIZE, offset },
    })
      .then((result) => {
        if (cancelled) return
        setData(result)
        setError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applied, offset])

  const columns: Column<PlayerRow>[] = [
    {
      key: 'email',
      title: 'Player',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          <div className="mono muted">{row.id}</div>
        </div>
      ),
    },
    {
      key: 'flags',
      title: 'Status',
      render: (row) => (
        <div className="row">
          {row.role === 'admin' && <span className="badge badge--accent">admin</span>}
          {row.banned && <span className="badge badge--danger">banned</span>}
          {row.ageConfirmedAt === null && <span className="badge badge--warn">no 18+</span>}
          {row.role === 'player' && !row.banned && row.ageConfirmedAt !== null && (
            <span className="badge badge--ok">active</span>
          )}
        </div>
      ),
    },
    { key: 'level', title: 'Lv.', numeric: true, render: (row) => row.accountLevel },
    { key: 'gold', title: '🪙', numeric: true, render: (row) => <Money currency="gold" value={row.gold} /> },
    { key: 'gems', title: '💎', numeric: true, render: (row) => <Money currency="gems" value={row.gems} /> },
    {
      key: 'energy',
      title: '⚡',
      numeric: true,
      render: (row) => <Money currency="energy" value={row.energy} />,
    },
    { key: 'created', title: 'Signed up', render: (row) => formatDate(row.createdAt) },
  ]

  const total = data?.total ?? 0

  return (
    <>
      <PageHead
        title="Players"
        subtitle="Profile, wallet, progress and assistant of every player. Currency is granted from the player card — through the ledger only."
      />

      <Card
        title={`Players${total > 0 ? ` · ${String(total)}` : ''}`}
        hint="Search by email address or by full id."
        padded={false}
        actions={
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              setOffset(0)
              setApplied(query.trim())
            }}
          >
            <input
              className="input"
              style={{ width: 260 }}
              placeholder="email or id"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn--sm" type="submit">
              Search
            </button>
            {applied !== '' && (
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => {
                  setQuery('')
                  setApplied('')
                  setOffset(0)
                }}
              >
                Reset
              </button>
            )}
          </form>
        }
      >
        <DataTable
          columns={columns}
          rows={data?.players ?? []}
          rowKey={(row) => row.id}
          loading={loading}
          error={error}
          empty={applied === '' ? 'No players yet' : `Nobody matches “${applied}”`}
          onRowClick={(row) => navigate({ name: 'player', id: row.id })}
        />

        {total > PAGE_SIZE && (
          <div className="row" style={{ justifyContent: 'space-between', padding: 'var(--gap) var(--gap-lg)' }}>
            <span className="muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <div className="row">
              <button
                className="btn btn--sm"
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Back
              </button>
              <button
                className="btn btn--sm"
                type="button"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </>
  )
}
